import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import TCPOGraph from "./TCPOGraph";
import Link from "common/Link";
import SelectInputWithIcon from "common/SelectInputWithIcon";

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const GridLayoutDiv = styled.div`
  display: grid;
  grid-template-areas:
    "side main-top main-top main-top main-top"
    "side main-bottom main-bottom main-bottom main-bottom";
  gap: 10px;
  min-height: 60vh;
  & > div {
    padding: 2px 0;
  }
  @media only screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
  }
`;

const CenteredDiv = styled.div`
  display: flex;
  justify-content: center;
  min-height: 50vh;
  align-items: center;
`;

const NothingToShow = () => <CenteredDiv>No data to show.</CenteredDiv>;

const isDataNull = ({ track, opt }) => track === null && opt === null;

const Span = styled.span`
  font-size: 14px;

  a{
    font-size: 14px;
  }
`;

const NetworkAvgTitle = () => (
  <Span>
    Network Average (Mbps)
  </Span>
);

const RTTTitle = () => (
  <Span>
    Broken down by connection{" "}
    <Link href="#viewDPILatency">Internet-side latency</Link>{" "}
    (Mbps, independent scales)
  </Span>
);

const ServicesTitle = () => (
  <Span>
    For <Link href="#viewDPITotalVolume">main services</Link>{" "}
    (Mbps)
  </Span>
);

const TCPOGraphs = ({ average, rtti, services, columns = [] }) => (
  <GridLayoutDiv>
    <div style={{ gridArea: "side" }}>
      <TCPOGraph
        title={<NetworkAvgTitle />}
        items={
          isDataNull(average)
            ? []
            : [{ nane: "average", label: false, ...average }]
        }
        missingText="Not enough samples"
        columns={columns}
      />
    </div>
    <div style={{ gridArea: "main-top" }}>
      <TCPOGraph
        title={<ServicesTitle />}
        items={services}
        missingText="Not enough samples for any service"
        columns={columns}
        independentScales={true}
      />
    </div>
    <div style={{ gridArea: "main-bottom" }}>
      <TCPOGraph
        title={<RTTTitle />}
        items={rtti}
        missingText="Not enough samples for any latency range"
        columns={columns}
        independentScales={true}
      />
    </div>
  </GridLayoutDiv>
);

const LegendsDiv = styled.div`
  display: flex;
  flex-layout: row;
  justify-content: space-between;
  padding: 0.2em 0.5em;
`;

const SamplesDiv = styled.div`
  display: flex;
  flex-layout: row;
  & > ul {
    list-decoration: none;
    display: inline-block;
  }
  & > ul > li {
    list-decoration: none;
    display: inline-block;
    margin: 0 0.5em;
  }
  & .color-sample {
    width: 2em;
    height: 15px;
    display: inline-block;
    margin: 0 0.2em;
  }
  & > ul > li,
  & .color-sample {
    line-height: 1.5em;
    vertical-align: middle;
  }
`;

const Sample = ({ legend, color, borderColor }) => (
  <li style={{ fontSize: '14px' }}>
    <div className="color-sample" style={{ backgroundColor: color, border: `1px solid ${borderColor}` }}>
      {" "}
    </div>
    {legend}
  </li>
);

const ExplanationDiv = styled.div`
  flex: 50% 1 1;
`;

const ResultLegends = ({ columns = [] }) => (
  <LegendsDiv>
    <ExplanationDiv>
      <code>n/s</code>: not statistically significant difference between the
      speed with and without TCPO (because of variability or not enough
      samples).
    </ExplanationDiv>
    <SamplesDiv>
      <ul>
        {columns.map((column) => (
          <Sample key={column.name} {...column} />
        ))}
      </ul>
    </SamplesDiv>
  </LegendsDiv>
);

const columns = [
  { field: "track", color: "var(--no-bta-color)", legend: "No TCPO", borderColor: "var(--no-bta-border-color)" },
  { field: "opt", color: "var(--bta-color)", legend: "TCPO", borderColor: "var(--bta-border-color)" },
];

const gotNoData = ({ average, rtti, services }) =>
  average === null && rtti.length === 0 && services.length === 0;

const MaybeTCPOGraphs = ({ columns, ...section }) =>
  gotNoData(section) ? (
    <NothingToShow />
  ) : (
    <TCPOGraphs {...section} columns={columns} />
  );

  const timeOptions = [
    { label: "1 day", value: 24 },
    { label: "2 days", value: 48 },
    { label: "3 days", value: 72 },
    { label: "1 week", value: 168 },
  ];

  const directionOptions = [
    {label: "Downloads", value: "download"},
    {label: "Uploads", value: "upload"}
  ]

  const ContextMenuWrapper = styled.div`
  width: 100%;
  margin-bottom: 20px;
  position: relative;
  display: flex;
  justify-content: space-between;

  .targetInput .input-group {
    margin-bottom: 0px;
  }

  @media (max-width: 1300px) {
    display: flex;
    flex-wrap: wrap;

    .targetInput {
      width: 100%;
    }
  }
`;

const SelectWrapper = styled.div`
  width: 150px;
  margin-right: 20px;
`;

const SelectsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

  const ContextMenu = ({ hours, onHoursChange, direction, setDirection }) => {
    return (
      <ContextMenuWrapper>
        <SelectsWrapper>
          <SelectWrapper className="directionSelect">
            <SelectInputWithIcon
              title="Direction"
              name="Direction"
              icon="import_export"
              selected={direction}
              onChange={({ target }) => setDirection(target.value)}
              options={directionOptions}
            />
          </SelectWrapper>
          <SelectWrapper className="timeSelect">
            <SelectInputWithIcon
              title="Time"
              name="time"
              icon="date_range"
              selected={hours}
              onChange={({ target }) => onHoursChange(parseInt(target.value))}
              options={timeOptions}
            />
          </SelectWrapper>
        </SelectsWrapper>
      </ContextMenuWrapper>
    );
  };

const Result = ({
  result,
  hours = 24,
  onHoursChange = doesNothing,
}) => {
  const { download, upload } = result;
  const [direction, setDirection] = useState("download");
  const section = direction === "download" ? {...download} : {...upload}

  return (
    <Wrapper>
      <ContextMenu
        hours={hours}
        onHoursChange={onHoursChange}
        direction={direction}
        setDirection={setDirection}
      />
      <div>
      {gotNoData(download) && gotNoData(upload) ? (
        <NothingToShow />
      ) : (
        <>
          <MaybeTCPOGraphs {...section} columns={columns} />
          <ResultLegends columns={columns} />
        </>
      )}
      </div>
    </Wrapper>
  );
};

export default Result;
